import { Component, OnDestroy, OnInit } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';

import {
  FirebaseMessaging,
  GetTokenOptions
} from '@capacitor-firebase/messaging';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, throwError } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize } from 'rxjs/operators';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { NavController } from '@ionic/angular';
import {
  LoginResponse,
  UserManagementService
} from '../services/user-management.service';
import { AuthService } from '../services/auth-service.service';
import { environment } from '../../environments/environment';
import { VersionService } from '../services/version.service';
import { ErrorToastService } from '../services/error-toast.service';

function ensureError(e: unknown): Error {
  return e instanceof Error
    ? e
    : new Error(typeof e === 'string' ? e : JSON.stringify(e));
}

const GROUP_CONST = 'Forwarders';
const EMAIL_PATTERN = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PASSWORD_SYMBOLS_PATTERN = /^\S*$/;

export interface ForwarderHmsMessagePlugin {
  getToken(opts: { appId: string }): Promise<{ token: string }>;
}
const ForwarderHmsMessage = registerPlugin<ForwarderHmsMessagePlugin>(
  'ForwarderHmsMessage'
);
export default ForwarderHmsMessage;

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit, OnDestroy {
  public passwordIsVisible: boolean = false;
  public forgotPasswordFormIsVisible: boolean = false;
  public loginProcessError: boolean = false;
  // Отдельно от loginProcessError: вход удался, но у пользователя нет роли экспедитора — показываем
  // собственное сообщение, а не «неверный логин/пароль».
  public accessDeniedError: boolean = false;
  public device_token = '';
  public platform = '';
  public isLoading: boolean = false;
  public version: string;

  public loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_PATTERN)
    ]),
    password: new FormControl('', [
      Validators.required,
      Validators.pattern(PASSWORD_SYMBOLS_PATTERN)
    ])
  });

  public forgotPasswordForm: FormGroup = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_PATTERN)
    ])
  });

  loginResponseCode?: number;

  private destroy$ = new Subject();

  constructor(
    private userManagementService: UserManagementService,
    private authService: AuthService,
    private versionService: VersionService,
    private router: Router,
    private navCtrl: NavController,
    private errorToastService: ErrorToastService,
    private route: ActivatedRoute
  ) {
    this.getDeviceToken();
    // accessDeniedError НЕ сбрасываем здесь: это отказ по роли (в т.ч. после имперсонации), а не
    // ошибка ввода. Автозаполнение полей браузером триггерит valueChanges и погасило бы сообщение
    // раньше, чем пользователь его увидит. Сбрасываем при новой попытке входа — в login().
    this.loginForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loginProcessError = false;
    });
  }

  ngOnInit(): void {
    this.versionService
      .getVersion()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.version = data.version;
      });

    // Отказ имперсонации без роли экспедитора уводит сюда с ?accessDenied=true — показываем то же
    // сообщение, что и при отказе обычного входа.
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['accessDenied']) {
          this.accessDeniedError = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public login() {
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;

    if (email && password) {
      this.isLoading = true;
      this.accessDeniedError = false;
      delete this.loginResponseCode;
      this.userManagementService
        .login(email, password, this.device_token, this.platform, GROUP_CONST)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isLoading = false)),
          catchError(err => {
            const safeError = ensureError(err);
            Sentry.captureException(safeError);
            this.handleFailedLogin(err?.status);
            return throwError(() => safeError);
          })
        )
        .subscribe((response: HttpResponse<LoginResponse>) => {
          const { body, status } = response;
          if (status === HttpStatusCode.Ok && body?.data?.access_token) {
            // Гейт по роли: пускаем в приложение только экспедиторов. Проверяем ДО сохранения
            // токена — при отказе токен не сохраняется, сессии не возникает.
            if (!this.authService.tokenHasForwarderRole(body.data.access_token)) {
              this.handleAccessDenied();
              return;
            }
            this.authService
              .login(
                body.data.access_token,
                body.data.refresh_token,
                body.data.refresh_expires_in
              )
              .then(() => {
                this.loginForm.reset();
              })
              .catch(error => {
                const safeError = ensureError(error);
                Sentry.captureException(safeError);
                this.handleFailedLogin();
                this.errorToastService.showError(
                  safeError.message || 'Ошибка при входе в систему'
                );
              });
          } else {
            this.handleFailedLogin(status);
          }
        });
    }
  }

  public forgotPassword() {
    this.forgotPasswordFormIsVisible = true;
  }

  public async getDeviceToken(): Promise<void> {
    const options: GetTokenOptions = {
      vapidKey: environment.firebase.vapidKey
    };
    this.platform = Capacitor.getPlatform();
    if (this.platform === 'web') {
      options.serviceWorkerRegistration =
        await navigator.serviceWorker.register('firebase-messaging-sw.js');
    }
    try {
      const { token } = await FirebaseMessaging.getToken(options);
      this.device_token = token;
    } catch (e) {
      Sentry.captureException(ensureError(e));
      this.device_token = '';
    }
    if (this.device_token === '') {
      try {
        const { token } = await ForwarderHmsMessage.getToken({
          appId: environment.hmsAppId
        });
        this.platform = 'huawei';
        this.device_token = token;
      } catch (error) {
        Sentry.captureException(ensureError(error));
        this.device_token = '';
      }
    }
  }

  public openRegistration() {
    this.router.navigate(['registration']);
  }

  public restorePassword() {
    this.router.navigate(['reset-password']);
  }

  public logVersionToSentry() {
    Sentry.captureMessage(
      `App version: ${this.version} @${new Date().toISOString()}`,
      {
        level: 'info'
      }
    );
  }

  private handleFailedLogin(responseCode?: number): void {
    this.loginForm.get('password')?.reset();
    this.loginProcessError = true;
    this.loginResponseCode = responseCode;

    if (responseCode === 406) {
      this.navCtrl.navigateForward(['registration'], {
        queryParams: { email: this.loginForm.get('email')?.value }
      });
    }
  }

  /**
   * Вход по учётным данным удался, но у пользователя нет роли экспедитора. Токен не сохранён и не
   * будет — показываем отдельное сообщение и сбрасываем пароль, оставаясь на экране входа.
   */
  private handleAccessDenied(): void {
    this.loginForm.get('password')?.reset();
    this.accessDeniedError = true;
  }
}
